import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sparkles, Megaphone, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { settingsAPI } from '../../lib/api';

// Logo versions
const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_5b134e2c-7890-49bc-80ca-89be776cbb68/artifacts/9duh96km_20220617%20Reshapers%20Logo%20Icon.png';
const LOGO_FULL = 'https://customer-assets.emergentagent.com/job_reshape-website/artifacts/frfzyqk8_20220617%20Reshapers%20Black%20Logo.png';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Solutions submenu items
const solutionsSubmenu = [
  { 
    name: 'Transformation', 
    path: '/solutions/transformation',
    icon: Sparkles,
    description: 'Navigate change and build resilient organizations',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
  },
  { 
    name: 'Marketing & Communications', 
    path: '/solutions/marketing-communications',
    icon: Megaphone,
    description: 'Connect, engage, and inspire your audiences',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
  },
  { 
    name: 'Team Building', 
    path: '/solutions/team-building',
    icon: Users,
    description: 'Create meaningful experiences that strengthen teams',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
];

const baseNavLinks = [
  { name: 'Home', path: '/' },
  { name: 'Solutions', path: '/solutions', hasSubmenu: true },
  { name: 'Impact', path: '/impact' },
  { name: 'Insights', path: '/insights' },
  { name: 'Open Sessions', path: '/workshops' },
  { name: 'About', path: '/about' },
];

export const Navbar = ({ blueWaveEnabled: blueWaveProp }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);
  const [toolsEnabled, setToolsEnabled] = useState(false);
  const [forcesEnabled, setForcesEnabled] = useState(false);
  const [blueWaveEnabled, setBlueWaveEnabled] = useState(false);
  const location = useLocation();
  const solutionsRef = useRef(null);
  const solutionsTimeoutRef = useRef(null);

  // Fetch tools enabled status
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsAPI.get();
        const data = res.data;
        setToolsEnabled(data.tools_library_enabled || false);
        setForcesEnabled(data.forces_enabled || false);
        setBlueWaveEnabled(data.blue_wave_enabled || false);
      } catch {
        // silent - nav falls back to base links
      }
    };
    fetchSettings();
  }, []);

  // Build nav links dynamically based on settings
  let navLinks = [...baseNavLinks];
  if (toolsEnabled) {
    navLinks = [...navLinks.slice(0, 4), { name: 'Tools', path: '/tools' }, ...navLinks.slice(4)];
  }
  if (forcesEnabled) {
    navLinks = [...navLinks.slice(0, -1), { name: 'FORCES', path: 'https://forces.reshapers.co', external: true }, ...navLinks.slice(-1)];
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSolutionsOpen(false);
    setIsMobileSolutionsOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Handle click outside for solutions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (solutionsRef.current && !solutionsRef.current.contains(event.target)) {
        setIsSolutionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSolutionsEnter = () => {
    if (solutionsTimeoutRef.current) {
      clearTimeout(solutionsTimeoutRef.current);
    }
    setIsSolutionsOpen(true);
  };

  const handleSolutionsLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => {
      setIsSolutionsOpen(false);
    }, 150);
  };

    const isHomePage = location.pathname === '/';
    const isBlueWave = (blueWaveProp || blueWaveEnabled) && isHomePage;
    const transparentNav = isBlueWave && !isScrolled;

    return (
    <>
      <header className={`${isBlueWave ? 'sticky top-0' : 'fixed top-2 sm:top-4 left-1/2 -translate-x-1/2 w-[calc(100%-1rem)] sm:w-[calc(100%-2rem)] max-w-7xl'} z-50 transition-all duration-500 ${
        isBlueWave
          ? 'bg-[#030F2E]/90 backdrop-blur-xl'
          : isScrolled 
            ? 'backdrop-blur-xl bg-white/95 shadow-lg border border-slate-200/50 rounded-full' 
            : 'backdrop-blur-xl bg-white/90 shadow-md border border-slate-200/50 rounded-full'
      }`}>
        {/* Blue Wave: 4-color thin line at bottom of navbar */}
        {isBlueWave && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] flex">
            <div className="flex-1" style={{ background: '#E0319A' }} />
            <div className="flex-1" style={{ background: '#FF6A1A' }} />
            <div className="flex-1" style={{ background: '#8FD03A' }} />
            <div className="flex-1" style={{ background: '#2BC0C2' }} />
          </div>
        )}
        <nav className="flex items-center justify-between px-3 sm:px-6 py-2 sm:py-3">
          {/* Logo with Animation */}
          <Link to="/" className="flex items-center group flex-shrink-0" data-testid="navbar-logo">
            {/* Mobile: Always show icon + text */}
            <div className="lg:hidden flex items-center gap-2">
              <img 
                src={LOGO_ICON} 
                alt="Reshapers" 
                className={`h-8 w-8 sm:h-10 sm:w-10 transition-transform group-hover:scale-110 ${isBlueWave ? 'brightness-0 invert' : ''}`}
              />
              <span className={`font-heading font-bold text-lg sm:text-xl hidden xs:inline ${isBlueWave ? 'text-white' : 'text-slate-900'}`}>Reshapers</span>
            </div>
            
            {/* Desktop: Always show full logo */}
            <div className="hidden lg:flex items-center h-10">
              <img 
                src={LOGO_FULL} 
                alt="Reshapers" 
                className={`h-10 transition-transform group-hover:scale-105 ${isBlueWave ? 'brightness-0 invert' : ''}`}
              />
            </div>
          </Link>

          {/* Desktop Navigation - Only show on lg screens and up */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              link.hasSubmenu ? (
                // Solutions with dropdown
                <div 
                  key={link.path}
                  ref={solutionsRef}
                  className="relative"
                  onMouseEnter={handleSolutionsEnter}
                  onMouseLeave={handleSolutionsLeave}
                >
                  <button
                    className={`flex items-center gap-1 text-sm font-medium transition-all whitespace-nowrap ${
                      location.pathname.startsWith('/solutions')
                        ? 'text-violet-600' 
                        : isBlueWave ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-violet-600'
                    }`}
                    onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                    data-testid="nav-solutions-dropdown"
                  >
                    {link.name}
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${isSolutionsOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div 
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200 ${
                      isSolutionsOpen 
                        ? 'opacity-100 translate-y-0 pointer-events-auto' 
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2 min-w-[320px]">
                      {/* View All Solutions Link */}
                      <Link
                        to="/solutions"
                        className="block px-4 py-2 text-sm text-slate-500 hover:text-violet-600 transition-colors border-b border-slate-100 mb-2"
                        data-testid="nav-view-all-solutions"
                      >
                        View All Solutions →
                      </Link>
                      
                      {solutionsSubmenu.map((solution) => (
                        <Link
                          key={solution.path}
                          to={solution.path}
                          className={`flex items-start gap-3 p-3 rounded-xl transition-all hover:${solution.bgColor} group`}
                          data-testid={`nav-solution-${solution.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className={`w-10 h-10 ${solution.bgColor} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                            <solution.icon className={solution.color} size={20} />
                          </div>
                          <div>
                            <p className={`font-medium text-slate-900 group-hover:${solution.color} transition-colors`}>
                              {solution.name}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {solution.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : link.external ? (
                // External link
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium transition-all link-underline whitespace-nowrap ${isBlueWave ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-violet-600"}`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ) : (
                // Regular nav link
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-all link-underline whitespace-nowrap ${
                    location.pathname === link.path 
                      ? 'text-violet-600' 
                      : isBlueWave ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-violet-600'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button - Desktop only */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <Link to="/contact">
              <Button 
                className="bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:opacity-90 rounded-full px-5 xl:px-6 text-sm btn-shine"
                data-testid="navbar-cta-btn"
              >
                Let's Talk
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button - Show on screens smaller than lg */}
          <button
            className={`lg:hidden p-2 transition-colors ${isBlueWave ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-violet-600'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div className={`lg:hidden fixed top-16 sm:top-20 left-2 right-2 sm:left-4 sm:right-4 z-50 transition-all duration-300 ${
        isMobileMenuOpen 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}>
        <div className="p-4 sm:p-6 backdrop-blur-xl bg-white/95 rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              link.hasSubmenu ? (
                // Solutions with expandable submenu
                <div key={link.path}>
                  <button
                    onClick={() => setIsMobileSolutionsOpen(!isMobileSolutionsOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      location.pathname.startsWith('/solutions')
                        ? 'bg-violet-50 text-violet-600' 
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid="mobile-nav-solutions-toggle"
                  >
                    {link.name}
                    <ChevronDown 
                      size={20} 
                      className={`transition-transform duration-200 ${isMobileSolutionsOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {/* Mobile Solutions Submenu */}
                  <div className={`overflow-hidden transition-all duration-300 ${
                    isMobileSolutionsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="pl-4 py-2 space-y-1">
                      <Link
                        to="/solutions"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-slate-500 hover:text-violet-600"
                        data-testid="mobile-nav-view-all-solutions"
                      >
                        View All Solutions
                      </Link>
                      {solutionsSubmenu.map((solution) => (
                        <Link
                          key={solution.path}
                          to={solution.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:${solution.bgColor}`}
                          data-testid={`mobile-nav-solution-${solution.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className={`w-8 h-8 ${solution.bgColor} rounded-lg flex items-center justify-center`}>
                            <solution.icon className={solution.color} size={16} />
                          </div>
                          <span className="text-slate-700 text-sm font-medium">{solution.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : link.external ? (
                // External mobile nav link
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-xl text-base font-medium transition-colors text-slate-700 hover:bg-slate-50"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ) : (
                // Regular mobile nav link
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path 
                      ? 'bg-violet-50 text-violet-600' 
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button 
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 text-white rounded-full py-6 text-base font-medium"
                data-testid="mobile-navbar-cta-btn"
              >
                Let's Talk Business
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
